import { Camera, CanvasEntitySelectionService, ICanvasRenderingEngine, InputSystem, PhysicsManager, Scene, SceneManagerService, generateId, listener, provideDefaultDebugCommands } from '@adaptiva/canvas';
import { Destroyer, EOperatingSystem, EnvService, isNullOrUndefined } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { IModalRef, ModalService } from '@adaptiva/core/modal';
import { Vector2, Vector3 } from '@adaptiva/core/space';
import { BaseThemeService } from '@adaptiva/core/themes';
import { UiService } from '@adaptiva/core/ui';
import { ETransferType, IMemoryPipelineNode, MemoryPipelineNode } from '@adaptiva/data-service';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Injector, Input, OnDestroy, Output, ViewChild, inject, runInInjectionContext } from '@angular/core';
import { Subscription } from 'rxjs';
import { ResizeSensorComponent } from '../../resize-sensor';
import { MemoryPipelineNodeEntity } from './entities';
import { MemoryPipelineDrawingService, MemoryPipelineFormattingService } from './services';

@Component({
	standalone: false,
	selector: 'ada-memory-pipeline',
	templateUrl: './memory-pipeline.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		Camera,
		Scene
	]
})
export class MemoryPipelineComponent extends Destroyer implements AfterViewInit, OnDestroy {

	@ViewChild('Canvas')
	public canvasRef: ElementRef<HTMLCanvasElement>;

	@ViewChild('CanvasEventTarget')
	public canvasEventTarget: ElementRef<HTMLElement>;

	protected descructibles: OnDestroy[] = [];

	@Input()
	public rootNodes: IMemoryPipelineNode[];

	@Output()
	public activeSelectionChange: EventEmitter<IMemoryPipelineNode> = new EventEmitter();

	public rootHolder: MemoryPipelineNodeEntity;

	public rootEntities: MemoryPipelineNodeEntity[] = [];

	protected sceneInjector!: Injector;

	protected camera: Camera;

	protected cursor: InputSystem.Cursor;

	protected keyboard: InputSystem.Keyboard<any>;

	protected scene: Scene;

	protected selection: CanvasEntitySelectionService;

	protected drawing: MemoryPipelineDrawingService;

	protected selectionSubscription!: Subscription;

	protected formatting: MemoryPipelineFormattingService;

	public get canvas(): HTMLCanvasElement {
		return this.canvasRef.nativeElement;
	}

	protected readonly debugModalId: string = 'adaptiva.memory-pipeline.debug.modal';

	protected debugModalRef!: IModalRef;

	protected modalService: ModalService = inject(ModalService);

	constructor(
		protected e1: ElementRef,
		protected engine: ICanvasRenderingEngine,
		protected injector: Injector,
		protected logger: LoggingService,
		protected physicsManager: PhysicsManager,
		protected sceneManager: SceneManagerService,
		protected themes: BaseThemeService,
		protected env: EnvService,
		public ui: UiService,
	) {
		super();
	}

	public resizeCanvas(_event: ResizeSensorComponent.IOutput): void {
		if (!this.engine.canvas)
			return;

		const _width: float = _event.width;
		const _height: float = _event.height;

		const _previousSize: Vector2 = new Vector2(this.engine.canvas.width, this.engine.canvas.height);
		const _delta: Vector2 = new Vector2(_width, _height).subtract(_previousSize).divide(2 * this.camera.position.z);

		this.engine.resize(_width, _height - 4);
		this.camera.navigate(this.engine.context, this.camera.position.copy().subtract(_delta));
	}

	public ngAfterViewInit(): void {
		this.loadScene();
	}

	public updateModel(_nodes: IMemoryPipelineNode[]): void {
		this.rootHolder.node.clients = _nodes;

		_nodes.forEach((_node: IMemoryPipelineNode) => {
			this.updateNode(_node);
		});

		if (this.selection.activeSelection) {
			const _selection: MemoryPipelineNodeEntity = this.selection.activeSelection as MemoryPipelineNodeEntity;

			this.activeSelectionChange.emit(_selection.node);
		}

		this.formatting.formatNodes(this.engine.context, this.rootHolder.node);
		this.formatting.applyRelativePositions(this.rootHolder);
	}

	public override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.clearDestructibles();
	}

	protected clearDestructibles(): void {
		this.descructibles.forEach((_desc: OnDestroy) => {
			_desc.ngOnDestroy();
		});
	}

	public updateNode(_node: IMemoryPipelineNode): void {
		let _entity: MemoryPipelineNodeEntity = this.engine.getEntity(_node.hostName);

		if (!_entity)
			_entity = this.createNodeEntity(_node);
		else
			Object.assign(_entity, { node: _node });

		if (_node?.sourceHostName.length > 0 && _node.sourceHostName !== _node.hostName) {
			const _parentEntity: MemoryPipelineNodeEntity = this.engine.getEntity(_node.sourceHostName);

			if (_parentEntity && _entity.parent !== _parentEntity)
				_entity.changeParent(_parentEntity);
		} else {
			// Attach to fake root
			if (_entity.parent !== this.rootHolder)
				_entity.changeParent(this.rootHolder);
		}

		_node.clients.forEach((_childNode: IMemoryPipelineNode) => {
			this.updateNode(_childNode);
		});
	}

	protected listenToSelections(): void {
		this.selectionSubscription?.unsubscribe();

		this.selectionSubscription = this.selection.activeSelection$.pipe(this.takeUntilDestroyed()).subscribe({
			next: (_selection: MemoryPipelineNodeEntity) => {
				if (!(_selection instanceof MemoryPipelineNodeEntity))
					return;

				this.activeSelectionChange.emit(_selection.node);
			}
		});
	}

	public loadScene(): void {
		this.createSceneInjector();

		if (!isNullOrUndefined(this.scene))
			this.sceneManager.unload(this.scene);

		this.scene = runInInjectionContext(this.sceneInjector, () => inject(Scene));
		this.scene.name = generateId();

		this.clearDestructibles();

		this.sceneManager.load(this.scene, {
			load: () => {
				this.engine.eventTarget = this.canvasEventTarget.nativeElement;

				this.descructibles.push(
					this.engine.render(this.canvas),
					listener(this.engine.eventTarget, 'contextmenu', e => e.preventDefault()),
					listener(this.canvas, 'contextmenu', e => e.preventDefault())
				);

				this.engine.eventTarget.focus();
				this.renderPipelineNodes();
				this.listenToSelections();

				this.descructibles.push(
					this.createToggleDebugListener()
				);
			},
			error: (e: Error) => {
				this.logger.error('MemoryPipelineComponent.loadScene', e)();
			}
		});
	}

	protected createSceneInjector(): void {
		this.sceneInjector = Injector.create({
			providers: [
				Scene,
				Camera,
				InputSystem.Cursor,
				InputSystem.Keyboard,
				CanvasEntitySelectionService,
				MemoryPipelineDrawingService,
				MemoryPipelineFormattingService
			],
			parent: this.injector
		});
	}

	protected renderPipelineNodes(): void {
		this.createEntities();

		const _fakeRoot: MemoryPipelineNodeEntity = this.createNodeEntity(
			new MemoryPipelineNode({
				hostName: '__fakeroot__',
				ipAddress: '100.100.100.100',
				sourceHostName: '',
				sourceIpAddress: '',
				transferType: ETransferType.LAN,
				operatingSystem: EOperatingSystem.LINUX,
				bytesDownloaded: 0,
				totalBytes: 0,
				percentComplete: 0,
				clients: []
			})
		);

		this.rootNodes.forEach((_root: IMemoryPipelineNode) => {
			_fakeRoot.node.clients.push(_root);
			const _entity: MemoryPipelineNodeEntity = this.createNodeAndChildren(_root);

			this.rootEntities.push(_entity);
			_fakeRoot.attach(_entity);
		});

		_fakeRoot.isHiding = true;

		this.rootHolder = this.drawing.rootEntity = _fakeRoot;

		this.formatting.formatNodes(this.engine.context, this.rootHolder.node);
		this.formatting.applyRelativePositions(this.rootHolder);

		this.scene.attach(this.rootHolder);
		this.attachEntities();
		this.resetCamera();
	}

	protected createEntities(): void {
		this.cursor = runInInjectionContext(this.sceneInjector, () => inject(InputSystem.Cursor));
		this.keyboard = runInInjectionContext(this.sceneInjector, () => inject(InputSystem.Keyboard));
		this.camera = runInInjectionContext(this.sceneInjector, () => inject(Camera));
		this.camera.layer = 0;
		this.selection = runInInjectionContext(this.sceneInjector, () => inject(CanvasEntitySelectionService));
		this.drawing = runInInjectionContext(this.sceneInjector, () => inject(MemoryPipelineDrawingService));
		this.formatting = runInInjectionContext(this.sceneInjector, () => inject(MemoryPipelineFormattingService));
	}

	protected attachEntities(): void {
		this.scene.attach(this.cursor);
		this.scene.attach(this.keyboard);
		this.scene.attach(this.camera);
		this.scene.attach(this.selection);
		this.scene.attach(this.drawing);
	}

	protected createNodeAndChildren(_node: IMemoryPipelineNode, _parent?: MemoryPipelineNodeEntity): MemoryPipelineNodeEntity {
		const _entity: MemoryPipelineNodeEntity = this.createNodeEntity(_node, _parent);

		_node.clients?.forEach((_child: IMemoryPipelineNode) => {
			const _childEntity: MemoryPipelineNodeEntity = this.createNodeAndChildren(_child, _entity);

			_entity.attach(_childEntity);
		});

		return _entity;
	}

	protected createNodeEntity(_node: IMemoryPipelineNode, _parent?: MemoryPipelineNodeEntity): MemoryPipelineNodeEntity {
		let _position: Vector2 = new Vector2();
		let _xPosition: int;

		if (_parent) {
			_xPosition = _parent.position.x;

			if (_parent.node.clients.length > 1) {
				const _index: int = _parent.node.clients.indexOf(_node);
				const _int: int = _index - ((_parent.node.clients.length - 1) / 2);

				_xPosition += _int * 200;
			}

			_position = new Vector2(_xPosition, _parent.position.y + 100);
		}

		const _entity: MemoryPipelineNodeEntity = MemoryPipelineNodeEntity.create<MemoryPipelineNodeEntity>(
			this.sceneInjector,
			{
				guid: _node.hostName,
				position: _position,
				node: _node
			}
		);

		_entity.setDimensions(this.engine.context);
		this.engine.attach(_entity);

		return _entity;
	}

	protected resetCamera(): void {
		this.camera.navigate(this.engine.context, Vector3.from({
			...Vector2.from(this.rootHolder.position).subtract({
				x: this.engine.canvas.width / 2,
				y: (this.ui.Spacing.SPACING_XXLARGE_NUMERIC * this.ui.BASE_UNIT_PX * 2) - 100
			}),
			z: 1
		}));
	}

	protected createToggleDebugListener(): OnDestroy {
		return listener(this.keyboard, InputSystem.Keyboard.events.KEYBIND_PRESSED, (e: CustomEvent<any>) => {
			if (e.detail === 'ToggleDebug') {
				if (this.debugModalRef) {
					this.modalService.remove(this.debugModalId);
					this.debugModalRef = undefined;
				} else {
					this.modalService.create({
						id: this.debugModalId,
						title: 'Debug Console',
						type: 'DebugConsoleComponent',
						elementInjector: Injector.create({
							name: 'MemoryPipeline > DebugConsoleComponent',
							parent: this.sceneInjector,
							providers: [
								provideDefaultDebugCommands(),
								{
									provide: ICanvasRenderingEngine,
									useValue: this.engine,
								}
							]
						}),
						hasBackdrop: false,
						placement: {
							top: '100px',
							left: '100px',
							display: 'block',
							width: '400px',
							height: '320px',
						}
					}).subscribe({
						next: (_modal: IModalRef): void => {
							this.debugModalRef = _modal;
							this.debugModalRef.ref.onDestroy(() => {
								this.debugModalRef = undefined;
							});
						}
					});
				}
			}
		});
	}

}
